// Shared helpers for products, ICPs and the content they drive

export const PRODUCT_COLORS = ['#ff8a5c', '#3f7fd9', '#8a6b4f', '#1f8f87', '#7a6fd8', '#e0567a'];

export const DEFAULT_CHANNELS = ['TikTok', 'Instagram', 'YouTube Shorts', 'Blog'];

// Avatar colors for ICPs, in order
const AVATAR_COLORS = [
    { bg: '#ffe1d6', fg: '#b4461f' },
    { bg: '#d9f3f1', fg: '#0B6F68' },
    { bg: '#e8e5fb', fg: '#5b4fc4' },
    { bg: '#fdf1dc', fg: '#9a5b07' },
    { bg: '#e3ecfb', fg: '#2f5fae' },
];

export const avatarColors = (index) => AVATAR_COLORS[Math.max(0, index) % AVATAR_COLORS.length];

export const initials = (name = '') => {
    const words = name.replace(/[^\p{L}\p{N}\s-]/gu, '').split(/[\s-]+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

export const productInitial = (product) => (product?.name || '?').trim().charAt(0).toUpperCase() || '?';

// "Night-shift Nadia" -> "Nadia", for "Create for Nadia"
export const shortName = (name = '') => name.trim().split(/\s+/).slice(-1)[0] || name;

// Create screens, keyed by route
export const CREATE_KINDS = {
    social: { label: 'Social posts', type: 'Social', contentType: 'Social posts' },
    blog: { label: 'Blog articles', type: 'Blog', contentType: 'Blog articles' },
    ads: { label: 'Ad copy', type: 'Ad', contentType: 'Ad copy' },
    video: { label: 'Video scripts', type: 'Video', contentType: 'Video scripts' },
};

export const kindForContentType = (contentType = '') => {
    const value = contentType.toLowerCase();
    if (/video|reel|short|stor/.test(value)) return 'video';
    if (/blog|article/.test(value)) return 'blog';
    if (/\bads?\b|ad copy|advert/.test(value)) return 'ads';
    return 'social';
};

// Post chip colors on the dashboard, by content type
export const TYPE_STYLES = {
    Video: { bg: '#ffecec', fg: '#c2383d' },
    Social: { bg: '#E3FBF8', fg: '#0B6F68' },
    Blog: { bg: '#efedfc', fg: '#5b4fc4' },
    Ad: { bg: '#fdf1dc', fg: '#9a5b07' },
};

// Bar colors for "What works for" an ICP
export const KIND_BAR_COLORS = { video: '#ff6b6b', social: '#2CD3C0', blog: '#7a6fd8', ads: '#e9a23b' };

const CHANNEL_SHORT = { Instagram: 'IG', 'YouTube Shorts': 'Shorts', YouTube: 'YouTube' };
export const shortChannel = (platform = '') => CHANNEL_SHORT[platform] || platform;

// The five setup steps shown on the dashboard and in the product switcher
export const getSetupSteps = (product, icpCount = product?.icpCount || 0) => {
    if (!product) return [];
    const done = product.setupDone || {};
    const channels = product.channels || [];
    const connected = channels.filter(channel => channel.connected).length;
    return [
        { id: 'profile', label: 'Product profile', done: !!done.profile, to: '/dashboard/profile' },
        { id: 'icps', label: 'ICPs', done: !!done.icps || icpCount > 0, to: '/dashboard/icps' },
        { id: 'voice', label: 'Brand voice', done: !!done.voice, to: '/dashboard/voice' },
        {
            id: 'channels',
            label: connected ? `Connect channels · ${connected} of ${channels.length}` : 'Connect channels',
            done: channels.length > 0 && connected === channels.length,
            to: '/dashboard#channels',
        },
        { id: 'plan', label: 'First content plan', done: !!done.plan, to: '/dashboard/plan' },
    ];
};

export const isOnboarding = (product) => product && product.setupStep && product.setupStep !== 'done';

// Product fields, in the order the review step and profile page show them
export const PRODUCT_FIELDS = [
    { id: 'name', label: 'Name', section: 'Basics', kind: 'text' },
    { id: 'oneLiner', label: 'One-liner', section: 'Basics', kind: 'text' },
    { id: 'category', label: 'Category', section: 'Basics', kind: 'text' },
    { id: 'platforms', label: 'Platforms', section: 'Basics', kind: 'list' },
    { id: 'pricing', label: 'Pricing', section: 'Basics', kind: 'text' },
    { id: 'features', label: 'Key features', section: 'Value & proof', kind: 'features' },
    { id: 'proof', label: 'Proof', section: 'Value & proof', kind: 'proof' },
    { id: 'competitors', label: 'Competitors', section: 'Value & proof', kind: 'list' },
    { id: 'targetCustomer', label: 'Target customer', section: 'Audience & goals', kind: 'text', gapLabel: "Who it's for first" },
    { id: 'goal', label: 'Content goal', section: 'Audience & goals', kind: 'text', gapLabel: 'Main goal for content' },
    { id: 'offer', label: 'Current offer', section: 'Audience & goals', kind: 'text', gapLabel: 'Current offer or promo' },
];

export const fieldLabel = (id) => PRODUCT_FIELDS.find(field => field.id === id)?.label || id;
export const gapLabel = (id, value) => {
    const field = PRODUCT_FIELDS.find(f => f.id === id);
    if (!field) return id;
    if (field.gapLabel) return field.gapLabel;
    return isEmptyValue(value) ? field.label : `Confirm ${field.label.toLowerCase()}`;
};

export const isEmptyValue = (value) => {
    if (value == null) return true;
    if (typeof value === 'string') return !value.trim();
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return value.rating == null && !(value.quotes || []).length;
    return false;
};

export const formatRating = (proof) => {
    if (!proof || proof.rating == null) return '';
    const count = proof.ratingCount ? ` (${formatCount(proof.ratingCount)})` : '';
    return `${Number(proof.rating).toFixed(1)} ★${count}`;
};

export const formatProof = (proof) => {
    if (!proof) return '';
    const parts = [];
    if (proof.rating != null) {
        parts.push(`${Number(proof.rating).toFixed(1)} ★${proof.ratingCount ? ` from ${Number(proof.ratingCount).toLocaleString()} ratings` : ''}`);
    }
    if (proof.quotes?.[0]) parts.push(`“${proof.quotes[0]}”`);
    return parts.join(' · ');
};

// Text shown for a field value in the review step and profile page
export const formatFieldValue = (id, value) => {
    if (isEmptyValue(value)) return '';
    if (id === 'features') return value.map(feature => feature.name).join(', ');
    if (id === 'proof') return formatProof(value);
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

// Parses what the user typed back into the field's shape
export const parseFieldValue = (id, text, previous) => {
    const list = text.split(',').map(item => item.trim()).filter(Boolean);
    if (id === 'platforms' || id === 'competitors') return list;
    if (id === 'features') {
        return list.map(name => (previous || []).find(feature => feature.name === name) || { name, detail: '' });
    }
    if (id === 'proof') {
        const rating = text.match(/(\d(?:\.\d)?)\s*★/);
        const count = text.match(/from\s+([\d,]+)/);
        const quote = text.match(/[“"]([^”"]+)[”"]/);
        return {
            rating: rating ? Number(rating[1]) : previous?.rating ?? null,
            ratingCount: count ? Number(count[1].replace(/,/g, '')) : previous?.ratingCount ?? null,
            quotes: quote ? [quote[1], ...(previous?.quotes || []).filter(q => q !== quote[1])] : previous?.quotes || [],
        };
    }
    return text.trim();
};

export const formatCount = (value) => {
    const number = Number(value) || 0;
    if (number >= 1000000) return `${(number / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    if (number >= 10000) return `${(number / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    return number.toLocaleString();
};

export const formatShortDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const SOURCE_ICONS = {
    landing: 'language',
    store: 'apps',
    upload: 'upload_file',
    pdf: 'picture_as_pdf',
    text: 'notes',
    quiz: 'quiz',
};

// Short name for "Synced from …" lines: a landing page's host, otherwise its title
export const sourceName = (source) => {
    if (source.type === 'landing' && source.url) {
        try {
            return new URL(/^https?:\/\//.test(source.url) ? source.url : `https://${source.url}`).hostname.replace(/^www\./, '');
        } catch (e) {
            return source.title;
        }
    }
    return source.title;
};

export const sourceIcon = (source) =>
    (source.type === 'upload' && /\.pdf$/i.test(source.title || '') ? SOURCE_ICONS.pdf : SOURCE_ICONS[source.type] || 'description');

// What the AI should know about the product, ICP and voice. Added to every generation prompt.
export const buildPromptContext = (product, icp) => {
    if (!product) return '';
    const lines = [`Product: ${product.name}${product.oneLiner ? ` — ${product.oneLiner}` : ''}`];
    if (product.category) lines.push(`Category: ${product.category}`);
    if (product.platforms?.length) lines.push(`Platforms: ${product.platforms.join(', ')}`);
    if (product.pricing) lines.push(`Pricing: ${product.pricing}`);
    if (product.offer) lines.push(`Current offer: ${product.offer}`);
    if (product.features?.length) {
        lines.push(`Key features: ${product.features.map(f => (f.detail ? `${f.name} (${f.detail})` : f.name)).join('; ')}`);
    }
    const proof = formatProof(product.proof);
    if (proof) lines.push(`Proof: ${proof}`);
    if (product.competitors?.length) lines.push(`Competitors: ${product.competitors.join(', ')}`);
    if (product.goal) lines.push(`Content goal: ${product.goal}`);

    if (icp) {
        lines.push('', `Write for this Ideal Customer Profile: ${icp.name}${icp.descriptor ? ` (${icp.descriptor})` : ''}`);
        if (icp.pains?.length) lines.push(`Pains: ${icp.pains.join('; ')}`);
        if (icp.goals?.length) lines.push(`Goals: ${icp.goals.join('; ')}`);
        if (icp.objections?.length) lines.push(`Objections to address: ${icp.objections.join('; ')}`);
        if (icp.vocabulary?.length) lines.push(`Words they use: ${icp.vocabulary.join(', ')}`);
    } else if (product.targetCustomer) {
        lines.push(`Target customer: ${product.targetCustomer}`);
    }

    const voice = product.voice;
    if (voice && (voice.summary || voice.avoid?.length || voice.notes)) {
        lines.push('', `Brand voice: ${voice.summary || 'see notes'}`);
        lines.push(`Casual (0) to formal (100): ${voice.formality ?? 30}. Playful (0) to serious (100): ${voice.seriousness ?? 35}.`);
        if (voice.avoid?.length) lines.push(`Never use: ${voice.avoid.join(', ')}`);
        if (voice.notes) lines.push(`Voice notes: ${voice.notes}`);
    }
    return lines.join('\n');
};

// Local dates as YYYY-MM-DD, weeks start on Monday
export const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
};

export const getWeekStart = (date = new Date()) => {
    const d = new Date(date);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return formatLocalDate(d);
};

export const addDays = (dateStr, days) => {
    const d = parseLocalDate(dateStr);
    d.setDate(d.getDate() + days);
    return formatLocalDate(d);
};
